// API client for public endpoints
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1'

/**
 * Resolve a stored image URL for the browser.
 *
 * Absolute URLs (and data: / blob: URIs) are returned as-is. A relative path
 * such as `/api/v1/uploads/leagues/1/...` is resolved against the same backend
 * the API calls go to: the origin of VITE_API_URL when that is an absolute URL
 * (production: images are served from the backend's host, not the site's).
 * When the API itself is same-origin (VITE_API_URL unset or relative, as in
 * local dev and the local demo, where Vite proxies /api), the path is left
 * relative so it goes through the same proxy. It used to fall back to the MCL
 * production backend here, so a local stack or the demo requested every
 * uploaded image from production.
 */
export function getImageUrl(url: string | null | undefined): string | null {
  if (!url) return null

  // If already absolute URL (or a data: / blob: URI), return as-is
  if (
    url.startsWith('http://') ||
    url.startsWith('https://') ||
    url.startsWith('data:') ||
    url.startsWith('blob:')
  ) {
    return url
  }

  // The backend origin, from VITE_API_URL when it is absolute
  // (e.g. "https://backend.scorelm.com/api/v1" -> "https://backend.scorelm.com").
  let backendBaseUrl = ''
  const apiUrl = import.meta.env.VITE_API_URL
  if (apiUrl) {
    const match = String(apiUrl).match(/^(https?:\/\/[^/]+)/)
    if (match) backendBaseUrl = match[1]
  }

  // Ensure URL starts with /
  const path = url.startsWith('/') ? url : `/${url}`
  return `${backendBaseUrl}${path}`
}

// Theme types

import {
  isPrivateSite,
  publicAuthHeaders,
  publicCredential,
  refusalFrom,
  reportPublicRefusal,
  requestTenantRefreshOnce,
  type PublicCredential,
} from './publicAccess'
import type {
  Team,
  NavMenuItem,
  SponsorLink,
  Venue,
  VenueDetailResponse,
  HomepageData,
  TeamStatsResponse,
  Player,
  PlayerStatsResponse,
  PlayerLeadersResponse,
  TeamLeadersResponse,
  SeasonStatConfig,
  Season,
  Tournament,
  TournamentDetails,
  GameDetailResponse,
  PlayerGameLogEntry,
  LiveEvent,
  GalleryImage,
  GalleryResponse,
  GalleryFilterOptions,
  DivisionArg,
  DivisionStandingsResponse,
} from './api/types'
export * from './api/types'


// ──────────────────────────────────────────────────────────────────────────
// Public-site sport context (#35 multi-sport: per-sport public site)
//
// The toggle in the top nav writes the fan's active sport to localStorage
// AND to this module-level slot. ApiClient.request() reads it and stamps
// `?sport=X` on every /public/* call so backend handlers can filter by
// sport_id. Endpoints that already pass their own ?sport= (tenant) keep
// theirs — the stamp only runs when the URL doesn't already have one.
//
// Paths excluded from auto-stamping (these are cross-sport or don't care):
//   /public/tenant       (caller passes its own sport)
//   /public/leagues      (cross-tenant)
//   /public/league-sports (lists which sports the tenant has)
//   /public/sports/:id   (sport-pack definition)
//   /public/manifest.json
//   /public/site-access  (the private site's access probe, registration step 33)
// ──────────────────────────────────────────────────────────────────────────

let activePublicSportId: string | null = null

export function setPublicSportContext(sportId: string | null) {
  activePublicSportId = sportId
}

export function getPublicSportContext(): string | null {
  return activePublicSportId
}

const PUBLIC_SPORT_AGNOSTIC_PREFIXES = [
  '/public/tenant',
  '/public/leagues',
  '/public/league-sports',
  '/public/sports/',
  '/public/manifest.json',
  // The private site's access probe (registration step 33): league-wide.
  '/public/site-access',
]

function shouldStampSport(endpoint: string): boolean {
  if (!endpoint.startsWith('/public/')) return false
  for (const p of PUBLIC_SPORT_AGNOSTIC_PREFIXES) {
    if (endpoint.startsWith(p)) return false
  }
  return true
}

function stampSportOnUrl(url: string, sportId: string | null): string {
  if (!sportId) return url
  // Don't double-stamp if the caller already provided one (some methods
  // pass an explicit sport in their own query string).
  const [path, query = ''] = url.split('?', 2)
  const params = new URLSearchParams(query)
  if (params.has('sport')) return url
  params.set('sport', sportId)
  return `${path}?${params.toString()}`
}

// Divisions (#583): the public `division` param is NEVER auto-stamped like
// `sport`. Detail pages must never be filtered, and a sport with fewer than
// two live divisions must send byte-identical requests (the Classic harness
// gates on the ordered request list). Controllers pass it explicitly, and a
// method given no division builds exactly the URL it built before.
export interface DivisionOpt {
  /** A division id, or 'all' where the endpoint supports it (API.md §4.2). Absent = today's request. */
  division?: DivisionArg | null
}

/** Append `division=` to a public path, only when one is given. */
export function withDivision(path: string, division: DivisionArg | null | undefined): string {
  if (division == null) return path
  return `${path}${path.includes('?') ? '&' : '?'}division=${encodeURIComponent(String(division))}`
}

export class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl
  }

  async request<T>(
    endpoint: string,
    options?: RequestInit & { bustCache?: boolean; noSportStamp?: boolean }
  ): Promise<T> {
    // Add cache-busting timestamp if requested
    let url = `${this.baseUrl}${endpoint}`
    // Multi-sport (#35): stamp ?sport=X on every public call. Public site
    // behaves like two separate sites — toggling Soccer/Basketball must swap
    // every list (teams, players, schedule, sponsors, gallery, etc.).
    // `noSportStamp`: an entity lookup that must find the entity in any sport
    // (#416, the deep-link sport resolver).
    if (!options?.noSportStamp && shouldStampSport(endpoint) && activePublicSportId) {
      url = stampSportOnUrl(url, activePublicSportId)
    }
    if (options?.bustCache) {
      const separator = url.includes('?') ? '&' : '?'
      url = `${url}${separator}_t=${Date.now()}`
    }
    
    // Add timeout to prevent hanging requests
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout
    
    // Private site (registration step 33, services/publicAccess.ts): on a
    // members-only league a /public/* call carries the member (else staff)
    // Bearer. On a public league `sent` is null and nothing is read, so the
    // request is byte-identical to before (I1).
    const isPublic = endpoint.startsWith('/public/')
    const sent: PublicCredential | null = isPublic ? publicCredential() : null

    try {
      // Measure only network time, not JSON parsing
      const networkStart = performance.now()
      const headers: Record<string, string> = {
        ...publicAuthHeaders(sent),
        ...options?.headers as Record<string, string>,
      }
      if (options?.body) {
        headers['Content-Type'] = 'application/json'
      }

      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers,
      })
      const networkDuration = performance.now() - networkStart

      clearTimeout(timeoutId)
      
      // Log slow network requests in development (network tab shows 3-26ms, so >100ms is suspicious)
      if (import.meta.env.DEV && networkDuration > 100) {
        console.warn(`Slow API network: ${endpoint} took ${networkDuration.toFixed(0)}ms (expected <50ms)`)
      }

      if (!response.ok) {
        // `status` lets controllers tell a typed 404 from other failures
        // (site templates §4.3) without string-matching; the message is
        // unchanged because classic pages still display it (#420).
        if (isPublic && (response.status === 401 || response.status === 503)) {
          // The members-only gate (registration step 33): record what it said
          // about the credential this call carried, so the site walls itself.
          // The call still rejects (never a promise that never settles: the
          // shared caches drop failed entries, a pending one would wedge them).
          const body = (await response.json().catch(() => null)) as { code?: string; reason?: string } | null
          const verdict = refusalFrom(response.status, body)
          if (verdict && isPrivateSite()) reportPublicRefusal(sent, verdict)
          // A cached tenant still says public while the gate refuses: refetch it once.
          if (response.status === 401 && (body?.code === 'members_only' || body?.code === 'private_site') && !isPrivateSite()) requestTenantRefreshOnce()
          throw Object.assign(new Error(`API error: ${response.statusText}`), {
            status: response.status,
            code: body?.code,
            reason: body?.reason,
            verdict,
          })
        }
        throw Object.assign(new Error(`API error: ${response.statusText}`), { status: response.status })
      }

      // 204 No Content / empty body → nothing to parse (see the admin request
      // helper below for the fuller note). Return undefined instead of letting
      // response.json() throw "Unexpected end of JSON input".
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return undefined as T
      }

      // Parse JSON separately to measure that time too
      const parseStart = performance.now()
      const data = await response.json()
      const parseDuration = performance.now() - parseStart
      
      if (import.meta.env.DEV && parseDuration > 100) {
        console.warn(`Slow JSON parse: ${endpoint} took ${parseDuration.toFixed(0)}ms`)
      }

      return data
    } catch (error) {
      clearTimeout(timeoutId)
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`Request timeout: ${endpoint}`)
      }
      throw error
    }
  }

  // Public API methods
  async getHomepage(opts: { bustCache?: boolean; sport?: string | null } & DivisionOpt = {}): Promise<HomepageData> {
    const qs = opts.sport ? `?sport=${encodeURIComponent(opts.sport)}` : ''
    return this.request(withDivision(`/public/homepage${qs}`, opts.division), { bustCache: !!opts.bustCache })
  }

  async getTeams(seasonId?: number, opts: DivisionOpt = {}) {
    const params = seasonId ? `?seasonId=${seasonId}` : ''
    return this.request<{ teams: Team[] }>(withDivision(`/public/teams${params}`, opts.division))
  }

  async getStandings(seasonId?: number, opts: DivisionOpt = {}) {
    const params = seasonId ? `?seasonId=${seasonId}` : ''
    return this.request(withDivision(`/public/standings${params}`, opts.division))
  }

  /** Stacked standings: one table per live division of the sport (API §4.3). Only called at 2+ live divisions. */
  async getDivisionStandings() {
    return this.request<DivisionStandingsResponse>('/public/standings/divisions')
  }

  async getTournamentStandings(tournamentId: number) {
    return this.request(`/public/tournaments/${tournamentId}/standings`)
  }

  async getGames(seasonId?: number, status?: string, opts: DivisionOpt = {}) {
    const params = new URLSearchParams()
    if (seasonId) params.append('seasonId', seasonId.toString())
    if (status) params.append('status', status)
    const query = params.toString() ? `?${params}` : ''
    return this.request(withDivision(`/public/games${query}`, opts.division))
  }

  async getGame(gameId: number) {
    return this.request<GameDetailResponse>(`/public/games/${gameId}`)
  }

  async getPlayers(params?: { search?: string; teamId?: number; position?: string; status?: 'active' | 'inactive' | 'all'; limit?: number; offset?: number } & DivisionOpt) {
    const queryParams = new URLSearchParams()
    if (params?.search) queryParams.append('search', params.search)
    if (params?.teamId) queryParams.append('teamId', params.teamId.toString())
    if (params?.position) queryParams.append('position', params.position)
    if (params?.status) queryParams.append('status', params.status)
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.offset) queryParams.append('offset', params.offset.toString())
    const query = queryParams.toString() ? `?${queryParams}` : ''
    return this.request<{ players: Player[]; total: number }>(withDivision(`/public/players${query}`, params?.division))
  }

  async getPlayer(playerId: number) {
    return this.request<{ player: Player }>(`/public/players/${playerId}`)
  }

  async getPlayerStats(playerId: number, seasonId?: number, tournamentId?: number) {
    const params = new URLSearchParams()
    if (seasonId) params.append('seasonId', seasonId.toString())
    if (tournamentId) params.append('tournamentId', tournamentId.toString())
    const query = params.toString() ? `?${params}` : ''
    return this.request<PlayerStatsResponse>(`/public/players/${playerId}/stats${query}`)
  }

  async getTeam(teamId: number) {
    return this.request<{ team: Team }>(`/public/teams/${teamId}`)
  }

  async getTeamStats(teamId: number, seasonId?: number, tournamentId?: number) {
    const query = new URLSearchParams()
    if (seasonId) query.append('seasonId', seasonId.toString())
    if (tournamentId) query.append('tournamentId', tournamentId.toString())
    const queryString = query.toString() ? `?${query.toString()}` : ''
    return this.request<TeamStatsResponse>(`/public/teams/${teamId}/stats${queryString}`)
  }

  async getTeamGames(teamId: number, seasonId?: number) {
    const params = seasonId ? `?seasonId=${seasonId}` : ''
    return this.request<{ games: any[] }>(`/public/teams/${teamId}/games${params}`)
  }

  async getPlayerGames(playerId: number, seasonId?: number) {
    const params = seasonId ? `?seasonId=${seasonId}` : ''
    return this.request<{ games: PlayerGameLogEntry[] }>(`/public/players/${playerId}/games${params}`)
  }

  async getTeamPlayers(teamId: number, seasonId?: number) {
    const params = seasonId ? `?seasonId=${seasonId}` : ''
    return this.request<{ players: Player[] }>(`/public/teams/${teamId}/players${params}`)
  }

  async getPlayerLeaders(params: {
    seasonId?: number; tournamentId?: number; teamId?: number; position?: string
    sortBy?: string; sortDir?: 'asc' | 'desc'
    limit?: number; offset?: number; minGames?: number
  } & DivisionOpt = {}) {
    const query = new URLSearchParams()
    if (params.seasonId) query.append('seasonId', params.seasonId.toString())
    if (params.tournamentId) query.append('tournamentId', params.tournamentId.toString())
    if (params.teamId) query.append('teamId', params.teamId.toString())
    if (params.position) query.append('position', params.position)
    if (params.sortBy) query.append('sortBy', params.sortBy)
    if (params.sortDir) query.append('sortDir', params.sortDir)
    if (params.limit) query.append('limit', params.limit.toString())
    if (params.offset !== undefined) query.append('offset', params.offset.toString())
    if (params.minGames) query.append('minGames', params.minGames.toString())
    const qs = query.toString() ? `?${query}` : ''
    return this.request<PlayerLeadersResponse>(withDivision(`/public/stats/leaders/players${qs}`, params.division))
  }

  async getTeamLeaders(params: {
    seasonId?: number; tournamentId?: number
    sortBy?: string; sortDir?: 'asc' | 'desc'
    limit?: number; offset?: number
  } & DivisionOpt = {}) {
    const query = new URLSearchParams()
    if (params.seasonId) query.append('seasonId', params.seasonId.toString())
    if (params.tournamentId) query.append('tournamentId', params.tournamentId.toString())
    if (params.sortBy) query.append('sortBy', params.sortBy)
    if (params.sortDir) query.append('sortDir', params.sortDir)
    if (params.limit) query.append('limit', params.limit.toString())
    if (params.offset !== undefined) query.append('offset', params.offset.toString())
    const qs = query.toString() ? `?${query}` : ''
    return this.request<TeamLeadersResponse>(withDivision(`/public/stats/leaders/teams${qs}`, params.division))
  }

  async getSeasonStatConfig(seasonId?: number, opts: DivisionOpt = {}) {
    const path = seasonId ? `/public/stats/season-config/${seasonId}` : '/public/stats/season-config/all'
    return this.request<{ config: SeasonStatConfig }>(withDivision(path, opts.division))
  }

  async getSeasons() {
    return this.request<{ seasons: Season[] }>('/public/seasons')
  }

  async getTournaments(seasonId?: number) {
    const params = seasonId ? `?seasonId=${seasonId}` : ''
    return this.request<{ tournaments: Tournament[] }>(`/public/tournaments${params}`)
  }

  async getTournament(tournamentId: number) {
    return this.request<TournamentDetails>(`/public/tournaments/${tournamentId}`)
  }

  async getScheduleFeatured() {
    return this.request<{ featured: Array<{
      id: number
      tournament_id: number
      display_order: number
      name: string
      year: number
      tournament_type: string
      tournament_status: string
    }> }>('/public/schedule-featured')
  }

  async getPage(slug: string, bustCache: boolean = false) {
    return this.request<{ page: {
      id: number
      title: string
      slug: string
      content: string | null
      excerpt: string | null
      featured_image: string | null
      page_type: string
      published_at: string | null
    } }>(`/public/pages/${slug}`, { bustCache })
  }

  async getPages(params?: { page_type?: string; limit?: number }, bustCache: boolean = false) {
    const queryParams = new URLSearchParams()
    if (params?.page_type) queryParams.append('page_type', params.page_type)
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    const query = queryParams.toString() ? `?${queryParams}` : ''
    return this.request<{ pages: Array<{
      id: number
      title: string
      slug: string
      excerpt: string | null
      featured_image: string | null
      page_type: string
      published_at: string | null
    }> }>(`/public/pages${query}`, { bustCache })
  }

  async getSponsors() {
    return this.request<{ levels: Array<{
      levelId: number | null
      levelName: string | null
      displayOrder: number
      sponsors: Array<{
        id: number
        name: string
        description: string | null
        logo_url: string | null
        links: SponsorLink[]
      }>
    }> }>('/public/sponsors')
  }

  async getSponsor(sponsorId: number) {
    return this.request<{ sponsor: {
      id: number
      name: string
      description: string | null
      logo_url: string | null
      level: { id: number; name: string } | null
      links: SponsorLink[]
    } }>(`/public/sponsors/${sponsorId}`)
  }

  // Gallery methods
  async getGallery(params?: {
    page?: number
    limit?: number
    season_id?: number
    tournament_id?: number
    team_id?: number
    player_id?: number
  }): Promise<GalleryResponse> {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.season_id) queryParams.append('season_id', params.season_id.toString())
    if (params?.tournament_id) queryParams.append('tournament_id', params.tournament_id.toString())
    if (params?.team_id) queryParams.append('team_id', params.team_id.toString())
    if (params?.player_id) queryParams.append('player_id', params.player_id.toString())
    const query = queryParams.toString() ? `?${queryParams}` : ''
    return this.request<GalleryResponse>(`/public/gallery${query}`)
  }

  async getGalleryHomepageFeatured(): Promise<{ images: GalleryImage[] }> {
    return this.request<{ images: GalleryImage[] }>('/public/gallery/homepage-featured')
  }

  async getGalleryFilterOptions(): Promise<GalleryFilterOptions> {
    return this.request<GalleryFilterOptions>('/public/gallery/filter-options')
  }

  /**
   * The private site's access probe (registration step 33): 200 when the
   * Bearer this call carries is admitted on a members-only league; a 401
   * members_only (with `reason`) or 503 check_unavailable otherwise, which
   * request() reports to services/publicAccess.ts. Called only on a
   * members-only league (SiteAccessProvider).
   */
  async getSiteAccess(): Promise<{ access: 'allowed' }> {
    return this.request<{ access: 'allowed' }>('/public/site-access', { noSportStamp: true })
  }

  // The nav-menu used to carry a `forms` array (1.0's form links), retired at
  // registration step R; a stale payload's extra key is ignored.
  async getNavMenu(): Promise<{ items: NavMenuItem[] }> {
    return this.request<{ items: NavMenuItem[] }>('/public/nav-menu')
  }

  async getLiveEvents(): Promise<{ liveEvents: LiveEvent[] }> {
    return this.request<{ liveEvents: LiveEvent[] }>('/public/live-events')
  }

  async getLiveEvent(slug: string): Promise<{ liveEvent: LiveEvent }> {
    return this.request<{ liveEvent: LiveEvent }>(`/public/live-events/${slug}`)
  }

  /**
   * Active *root* venues for the public Venues page. The backend filters out
   * children — each root carries a `children` summary + `child_count`.
   * Gated by the venues site section (frontend route guard).
   */
  async getVenues(): Promise<Venue[]> {
    const res = await this.request<{ venues: Venue[] }>('/public/venues')
    return res.venues
  }

  /**
   * Single root venue + its children + every game played at the root or any
   * child. Gated by the venues site section. The backend 404s on child ids — the
   * UI links to the root instead (see VenueLink).
   */
  async getVenue(id: number): Promise<VenueDetailResponse> {
    return this.request<VenueDetailResponse>(`/public/venues/${id}`)
  }

  // ============================================================
  // Ticketing — public discovery + checkout + branded ticket page
  // ============================================================
  // Mirrors backend/src/routes/public/ticket-sales.ts. Tenant-scoped via
  // hostname (resolveTenant middleware). Never receives stripe_* IDs or
  // platform-fee internals from the API; safe to call from the public site.

  async listTicketSales() {
    return this.request<{ ticket_sales: TicketSaleSummary[] }>(
      '/public/ticket-sales'
    )
  }

  async getTicketSale(slug: string) {
    return this.request<TicketSaleDetailResponse>(
      `/public/ticket-sales/${encodeURIComponent(slug)}`
    )
  }

  async checkoutTicketSale(
    slug: string,
    payload: {
      tickets: Array<{ ticket_type_id: number; quantity: number }>
      buyer: { name?: string; email: string; phone?: string }
    }
  ) {
    // We need granular error handling here (503 = stripe_not_configured shows
    // a friendly "contact the league" copy; 409 = capacity gone; etc.), so
    // bypass `this.request` and roll our own fetch to inspect the status
    // code and structured body.
    const res = await fetch(
      `${this.baseUrl}/public/ticket-sales/${encodeURIComponent(slug)}/checkout`,
      {
        method: 'POST',
        // A members-only league's checkout is gated (registration step 33):
        // the member (else staff) Bearer; nothing on a public league.
        headers: { ...publicAuthHeaders(), 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      }
    )
    const body = await res.json().catch(() => ({}))
    if (!res.ok) {
      const err: any = new Error(body.message || `Checkout failed (${res.status})`)
      err.status = res.status
      err.code = body.error // e.g. 'stripe_connect_not_configured', 'Conflict'
      throw err
    }
    return body as { checkout_url: string; order_id: number }
  }

  async getTicket(token: string) {
    return this.request<TicketPageResponse>(
      `/public/tickets/${encodeURIComponent(token)}`
    )
  }

  /**
   * Fetch the order-wallet bundle (every ticket on a multi-ticket order,
   * QR-rendered, ready to display on /tickets/order/:token). The token is
   * the HMAC-signed order_token stamped at issuance time; the backend
   * also enforces tenant scoping so cross-tenant probes return 404.
   */
  async getOrderWallet(token: string) {
    return this.request<OrderWalletResponse>(
      `/public/tickets/order/${encodeURIComponent(token)}`
    )
  }
}

// ---- Ticketing public-API response shapes ----------------------------------
// Keep these next to the methods that consume them. Mirrors the backend
// publicSaleShape() / publicTypeShape() exactly.

export interface TicketSaleSummary {
  id: number
  sport_id: string | null
  scope_type: string | null
  game_id: number | null
  season_id: number | null
  tournament_id: number | null
  name: string
  slug: string
  description: string | null
  status: string
  sales_open_at: string | null
  sales_close_at: string | null
  event_date: string | null
  event_time: string | null
  event_timezone: string | null
  venue_id: number | null
  venue_name_override: string | null
  /** Resolved venue name — the public endpoints set this to
   *  (override || venues.name). Frontend reads it so the /tickets
   *  cards can show the actual venue without an N+1 fetch. */
  venue_name?: string | null
  hero_image_url: string | null
  /** Cheapest active ticket type's price, in cents. Surfaced on the list
   *  endpoint so /tickets cards can show "From $X". Null when the sale
   *  has zero active types. Not populated on the detail response (that
   *  one returns the full ticket_types array). */
  min_price_cents?: number | null
  min_price_currency?: string | null
}

export interface TicketTypePublic {
  id: number
  name: string
  description: string | null
  price_cents: number
  /** Dual pricing: how much cheaper the buyer is by buying online instead
   *  of at the door. Non-null only when the operator has set a door
   *  price higher than the online price. Otherwise null (no callout).
   *  The raw door price is deliberately never exposed publicly. */
  door_price_savings_cents: number | null
  currency: string
  is_active: boolean
  /** null = no cap configured (unlimited). number = max - sold (>= 0). */
  remaining: number | null
}

export interface TicketSaleDetailResponse {
  ticket_sale: TicketSaleSummary
  ticket_types: TicketTypePublic[]
  venue_name: string | null
}

/**
 * Order-wallet payload — every ticket on one order, rendered as a single
 * branded page at /tickets/order/:token. Used by N>1 orders so the buyer
 * doesn't get N separate ticket URLs in their email/SMS. Single-ticket
 * orders bypass the wallet entirely (they get the direct ticket URL).
 *
 * `tickets[].ticket_url` is the per-ticket /tickets/t/<token> URL the
 * wallet's "Share this ticket" copy-to-clipboard button uses.
 */
export interface OrderWalletResponse {
  order: {
    id: number
    buyer_name: string | null
    buyer_email: string | null
    total_cents: number
    payment_channel: 'online' | 'door_cc' | 'door_cash' | 'comp' | string
    payment_status: 'pending' | 'paid' | 'failed' | 'refunded' | 'partially_refunded' | string
    refunded_cents: number
    created_at: string
  }
  sale: {
    id: number
    name: string
    slug: string
    description: string | null
    event_date: string | null
    event_time: string | null
    event_timezone: string | null
    venue_name: string | null
    hero_image_url: string | null
  }
  /**
   * League branding minus theme colors — the LeagueProvider already has
   * primary/secondary on hand, no need to double-serialize.
   */
  league: {
    id: number
    name: string
    logo_url: string | null
  }
  tickets: Array<{
    id: number
    ticket_token: string
    short_code: string
    attendee_name: string | null
    status: 'issued' | 'scanned' | 'voided' | 'refunded' | string
    scanned_at: string | null
    ticket_type_name: string
    ticket_type_description: string | null
    price_cents: number
    currency: string
    qr_svg: string | null
    /** Per-ticket public URL the "Share this ticket" button copies. */
    ticket_url: string
  }>
}

export interface TicketPageResponse {
  ticket: {
    id: number
    token: string
    short_code: string
    attendee_name: string | null
    status: 'issued' | 'scanned' | 'voided' | 'refunded' | string
    scanned_at: string | null
    ticket_type_name: string
    ticket_type_description: string | null
    price_cents: number
    currency: string
    /** Full <svg>…</svg> string rendered by backend qr-svg service. */
    qr_svg: string | null
  }
  sale: {
    id: number
    name: string
    slug: string
    event_date: string | null
    event_time: string | null
    event_timezone: string | null
    venue_name: string | null
    hero_image_url: string | null
  }
  order: {
    id: number
    buyer_name: string | null
    buyer_email: string | null
    created_at: string
  } | null
}

// ============================================================
// Ticketing — shared types for the admin and its consumers.
// Mirrors the shapes returned by backend/src/routes/admin/ticket-sales.ts
// and backend/src/routes/admin/ticket-scanner.ts.
// ============================================================

export type TicketSaleStatus = 'draft' | 'open' | 'paused' | 'closed' | 'archived'
export type TicketSaleScope = 'game' | 'season' | 'tournament' | 'standalone'
export type TicketOrderChannel = 'online' | 'door_cash' | 'door_cc' | 'comp'
export type TicketOrderStatus = 'pending' | 'paid' | 'failed' | 'refunded' | 'partially_refunded'
export type TicketStatus = 'issued' | 'scanned' | 'voided' | 'refunded'
export type ScannerResultCode =
  | 'valid_first_scan'
  | 'duplicate'
  | 'invalid_token'
  | 'wrong_event'
  | 'voided'
  | 'refunded'

export interface TicketSale {
  id: number
  league_id: number
  sport_id: string
  scope_type: TicketSaleScope
  game_id: number | null
  season_id: number | null
  tournament_id: number | null
  status: TicketSaleStatus
  name: string
  slug: string
  description: string | null
  sales_open_at: string | null
  sales_close_at: string | null
  event_date: string | null
  event_time: string | null
  event_timezone: string | null
  venue_id: number | null
  venue_name_override: string | null
  hero_image_url: string | null
  created_at: string
  updated_at: string
}

export interface TicketSaleListRow extends TicketSale {
  paid_order_count: string | number
  active_ticket_count: string | number
  gross_cents: string | number
}

export interface TicketType {
  id: number
  ticket_sale_id?: number
  name: string
  description: string | null
  price_cents: number
  // Optional door-only price override. null = door uses price_cents.
  // Only surfaced on ADMIN responses (public routes never SELECT this).
  door_price_cents: number | null
  currency: string
  max_quantity: number | null
  sold_quantity: number
  display_order: number
  is_active: boolean
  created_at?: string
  updated_at?: string
}

export interface TicketSaleDetail {
  ticket_sale: TicketSale
  // Resolved display name: venue_name_override, else the picked venue's name.
  venue_name?: string | null
  ticket_types: TicketType[]
  order_summary: {
    paid_count: string | number
    pending_count: string | number
    refunded_count: string | number
    gross_cents: string | number
    fees_cents: string | number
    refunded_cents: string | number
    // Per-channel revenue split — populates the admin overview widget.
    gross_cents_door_cash: string | number
    gross_cents_door_cc: string | number
    gross_cents_online: string | number
    gross_cents_comp: string | number
  }
  scan_summary: {
    issued_count: string | number
    scanned_count: string | number
    voided_count: string | number
  }
}

export interface TicketSaleCreatePayload {
  name: string
  slug: string
  description?: string | null
  scope_type: TicketSaleScope
  game_id?: number | null
  season_id?: number | null
  tournament_id?: number | null
  sales_open_at?: string | null
  sales_close_at?: string | null
  event_date?: string | null
  event_time?: string | null
  event_timezone?: string | null
  venue_id?: number | null
  venue_name_override?: string | null
  hero_image_url?: string | null
}

export interface TicketTypeWritePayload {
  name: string
  description?: string | null
  price_cents: number
  // Optional. Pass null explicitly (via PUT) to clear the override.
  door_price_cents?: number | null
  currency?: string
  max_quantity?: number | null
  display_order?: number
  is_active?: boolean
}

export interface TicketOrderRow {
  id: number
  buyer_email: string | null
  buyer_phone: string | null
  buyer_name: string | null
  payment_channel: TicketOrderChannel
  payment_status: TicketOrderStatus
  subtotal_cents: number
  platform_fee_cents: number
  total_cents: number
  refunded_cents: number
  fee_billed_at: string | null
  fee_invoice_id: string | null
  notes: string | null
  created_at: string
}

export interface TicketOrderDetail {
  order: TicketOrderRow & {
    ticket_sale_id: number
    league_id: number
    stripe_checkout_session_id: string | null
    stripe_payment_intent_id: string | null
    sold_by_user_id: number | null
    updated_at: string
    /**
     * HMAC-signed wallet-URL token (issue #91). Present for every order
     * created after migration 060; nullable for older test rows. The
     * admin Orders tab surfaces /tickets/order/<order_token> when this
     * is set, primarily for N>1 orders but harmless for N=1.
     */
    order_token: string | null
  }
  tickets: Array<{
    id: number
    ticket_token: string
    short_code: string
    attendee_name: string | null
    status: TicketStatus
    scanned_at: string | null
    scanned_by_user_id: number | null
    scan_count: number
    voided_reason: string | null
    ticket_type_name: string
  }>
  scans: Array<{
    id: number
    ticket_id: number
    result: string
    scanned_by_user_id: number | null
    scanned_at: string
    user_agent: string | null
  }>
}

export interface DoorSalePayload {
  tickets: Array<{ ticket_type_id: number; quantity: number }>
  buyer?: { name?: string | null; email?: string | null; phone?: string | null }
  channel: 'door_cash' | 'door_cc' | 'comp'
  notes?: string | null
}

export interface DoorSaleResponse {
  order_id: number
  // Present for cash + comp (tickets issued immediately).
  order?: { id: number; payment_status: TicketOrderStatus; total_cents: number; platform_fee_cents: number }
  tickets?: Array<{ id: number; ticket_token: string; short_code: string; ticket_type_name: string }>
  // Present for door_cc (Stripe checkout pending payment).
  checkout_url?: string
  // Short backend redirect URL that the QR actually encodes
  // (e.g. https://backend.scorelm.com/pay/123). Use this in the
  // "or open this link" fallback shown next to the QR, not the long
  // Stripe URL. Both resolve to the same Stripe Checkout.
  short_url?: string
  qr_svg?: string
  short_code?: string | null
}

export interface ScannerResult {
  result: ScannerResultCode
  first_scanned_at?: string | null
  ticket?: {
    id: number
    attendee: string | null
    ticket_type_name: string | null
  }
}

export interface StripeConnectStatus {
  configured: boolean
  account_id: string | null
  status: 'connected' | 'pending' | 'disconnected' | string
  connected_at: string | null
}

// ============================================
// Bulk CSV Import types
// ============================================


export const api = new ApiClient()


